// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xCheckoutActions, xCheckoutState } from "@eisinc/App/Store/Reducers";
import TextArea, { TextAreaProps } from "@eisinc/MobiusOverrides/TextArea";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import setNotes from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetNotes";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import setNotesOnePageCheckout from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetNotes";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { ChangeEvent, Component } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const { cartId } = getQueryStrings(state);
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const page = getCurrentPage(state);
    const isOnePageCheckoutPage = page.type === "OnePageCheckoutPage";
    return {
        notes: state.xCheckout.notes || cart?.notes,
        isOnePageCheckoutPage,
        // XNG-Change
        checkoutNotes: state.xCheckout.notes,
    };
};

const mapDispatchToProps = {
    setNotes,
    setNotesOnePageCheckout,
    // XNG-Change
    setCheckoutNotes: (notes: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({
            type: "xCheckout/setNotes",
            notes,
        });
    },
    updateCart,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    TextAreaProps;

export interface CheckoutShippingOrderNotesEntryStyles {
    textArea?: TextAreaProps;
}

export const checkoutShippingOrderNotesEntryStyles: CheckoutShippingOrderNotesEntryStyles = {
    textArea: {
        // css: css`
        //     min-width: 100%;
        //     max-width: 100%;
        //     min-height: 50px;
        // `,
    },
};

const styles = checkoutShippingOrderNotesEntryStyles;

interface State {
    notes?: string;
}

class CheckoutShippingOrderNotesEntry extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            notes: this.props.notes,
        };
        if (this.props.notes && this.props.notes !== this.props.checkoutNotes) {
            this.props.setCheckoutNotes(this.props.notes);
        }
    }

    notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            notes: event.currentTarget.value,
        });

        if (this.props.isOnePageCheckoutPage) {
            this.props.setNotesOnePageCheckout({
                notes: event.currentTarget.value,
            });
        } else {
            this.props.setNotes({
                notes: event.currentTarget.value,
            });
        }
        // XNG-Change
        this.props.setCheckoutNotes(event.currentTarget.value);
    };

    // XNG-Changes
    notesBlurHandler = () => {
        this.props.updateCart();
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        if (typeof state.notes === "undefined" && typeof props.notes !== "undefined") {
            return {
                notes: props.notes,
            };
        }
        return state;
    }

    render() {
        if (typeof this.state.notes === "undefined") {
            return null;
        }

        return (
            <TextArea
                {...styles.textArea}
                label={this.props.isOnePageCheckoutPage ? translate("Order Notes") : translate("Add Order Notes")}
                placeholder={translate("Enter your order notes")}
                value={this.state.notes}
                onChange={this.notesChangeHandler}
                data-test-selector="orderNotesTextArea"
                maxLength={4000}
                optional
                // XNG-Changes
                onBlur={this.notesBlurHandler}
            />
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingOrderNotesEntry),
    definition: {
        group: "Checkout - Shipping",
        displayName: "Order Notes Entry",
        allowedContexts: ["CheckoutShippingPage", "OnePageCheckoutPage"],
    },
};

export default widgetModule;
